import { MathUtil } from "@base/math/MathUtil";
import { IArticleFeatureHandler, IArticleFeatureTemplate } from "@biz/definitions/ArticleFeatureDefinitions";
import { FeatureFormatters } from "@biz/definitions/ArticleFeatureFormatter";
import { SojoWoodType } from "@biz/definitions/LoungeDefinitions";
import { SunSailType, SunSailTypeNameShort } from "@biz/definitions/SunSailDefinitions";
import { getSunSailTypeForShortName } from "@biz/definitions/SunSailDefinitions.util";
import { ProtectiveCoverLogic } from "@biz/items/sunSail/logic/ProtectiveCoverLogic";
import { LocaContext } from "@biz/language/LocaContext";
import { AdditionalArticleInfo } from "@biz/logic/AdditionalArticleInfo";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { Measure } from "@biz/logic/Measure";
import { getTieBackVariant } from "@biz/orders/BeosVarianten";

export const articleFeatureHandlers = {
  lengthInM: {
    unit: Measure.m,
    formatValue: (val) => "" + (val as number) + Measure.m,
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.lengthInM(context, (val as number) || 0)
  },
  tieBackLength: {
    unit: Measure.m,
    formatValue: (val) => "" + (val as number) + Measure.m,
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.lengthInM(context, (val as number) || 0),
    beosVariants: (val) => (val ? { "42": getTieBackVariant(val as number) } : undefined)
  },
  lengthToMM: {
    unit: Measure.mm,
    formatValue: (val) => "" + (val as number) + Measure.mm,
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.lengthToMM(context, (val as number) || 0)
  },
  diamterId: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.diameterId(context, val),
  },
  diameterInMM: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.diameterMM(context, val),
    formatValue: (val) => "⌀" + (val as number) + Measure.mm,
    sort: (a, b) => ((a || 0) as number) - ((b || 0) as number)
  },
  areaInM2: {
    unit: Measure.m2,
    formatValue: (val) => "" + (val as number) + Measure.m2,
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.areaM2(context, (val as number) || 0)
  },
  colorId: {
    formatValue: (val, context) => AdditionalArticleInfo.getColorName("" + val, context.lc),
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.colorId(context, val?.toString() || "")
  },
  rainCut: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.rainCut(context, !!val)
  },
  sojoWoodType: {
    additionalArticleInfo: (val, context) => ({ header: context.lc.t("Sojo_Wood"), text: context.lc.t(LocaContext.connect("Sojo_Wood", val as SojoWoodType)) })
  },
  logo: {
    additionalArticleInfo: (val, context) => val === undefined ? undefined : { header: context.lc.t("ArticleInfo_PrintLogo"), text: "" + val }
  },
  volumeInML: {
    unit: "ml",
    formatValue: (val) => "" + (val as number) + "ml",
    additionalArticleInfo: (val, context) => ({ header: context.lc.t("ArticleInfo_Volume"), text: "" + val + "ml" })
  },
  sst: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "ArticleInfo_System", val?.toString() || "")
  },
  quality: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Quality", val?.toString() || "")
  },
  axleLength: {
    unit: Measure.mm,
    values: () => {
      return { type: "slider", min: 3, max: 13, step: 0.01, unitMultiplicator: 1000, decimals: 0 }
    },
    mul: (v, store) => {
      const sst = store.sst;
      const sunSailType = getSunSailTypeForShortName(("" + sst) as SunSailTypeNameShort) ?? SunSailType.SolidayC;
      return typeof v === "number" ? ProtectiveCoverLogic.calcCoverLength(sunSailType, v) : 1
    },
    additionalArticleInfo: (val, context, store) => {
      const system = store.sst;
      const s = getSunSailTypeForShortName(("" + system) as SunSailTypeNameShort);
      if (s === undefined) {
        console.warn("Invalid protective system: ", system);
      }

      const sunSailType = s ?? SunSailType.SolidayC;
      return [
        { header: context.lc.t("ArticleInfo_AxleLength"), text: Measure.toMM(val as number) },
        { header: context.lc.t("ArticleInfo_ProtectiveCoverLength"), text: ProtectiveCoverLogic.calcProtectiveCoverLengthText(val as number, sunSailType) }
      ]
    },
    formatValue: (val) => Measure.toMM(val as number),
  },
  netShapeType: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Shape", context.lc.t(LocaContext.connect("SC_ShapeType", val as string)))
  },
  curtainShapeType: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Shape", context.lc.t(LocaContext.connect("Curtain_Shape", val as string)))
  },
  curtainSuspensionType: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Suspension", context.lc.t(LocaContext.connect("Curtain_Suspension", val as string)))
  },
  curtainConfectionType: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Confection_Top", context.lc.t(LocaContext.connect("Curtain_Confection", val as string)))
  },
  curtainConfectionBottomType: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.headerAndText(context, "Curtain_ConfectionBottom", context.lc.t(LocaContext.connect("Curtain_ConfectionBottom", val as string)))
  },
  startTerminal: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.startTerminal(context, val?.toString() || ""),
  },
  endTerminal: {
    additionalArticleInfo: (val, context) => AdditionalArticleInfo.endTerminal(context, val?.toString() || "")
  },
  angleLeft: {
    unit: "°",
    formatValue: (val) => "" + (val as number) + "°",
    additionalArticleInfo: (val, context) => val ? AdditionalArticleInfo.headerAndText(context, "ArticleInfo_AngleLeft", (val as number).toFixed(1) + "°") : undefined
  },
  angleRight: {
    unit: "°",
    formatValue: (val) => "" + (val as number) + "°",
    additionalArticleInfo: (val, context) => val ? AdditionalArticleInfo.headerAndText(context, "ArticleInfo_AngleRight", (val as number).toFixed(1) + "°") : undefined
  },
  notchSide: {
    additionalArticleInfo: (val, context) => val ? AdditionalArticleInfo.headerAndText(context, "ArticleInfo_Notch", val as string) : undefined
  },
} satisfies Record<string, IArticleFeatureHandler>;

export const shopTemplates = {
  prallSchutz: {
    length: articleFeatureHandlers.lengthInM,
    colorId: articleFeatureHandlers.colorId
  },
  spannSeil: {
    colorId: articleFeatureHandlers.colorId,
    diameter: articleFeatureHandlers.diameterInMM,
    length: {
      mul: true,
      ...articleFeatureHandlers.lengthToMM,
      values: { type: "slider", min: 3, max: 50, step: 1, decimals: 0 },
      defaultValue: 10,
    }
  },
  spannSeilSpecial: {
    colorId: articleFeatureHandlers.colorId,
    diameter: articleFeatureHandlers.diameterInMM,
    length: {
      mul: true,
      ...articleFeatureHandlers.lengthInM,
      values: { type: "slider", min: 3, max: 50, step: 1, decimals: 0 },
      defaultValue: 10,
    }
  },
  drahtSeil: {
    diameter: articleFeatureHandlers.diameterInMM,
    length: {
      ...articleFeatureHandlers.lengthInM,
      mul: true,
      values: { type: "slider", min: 3, max: 50, step: 1, decimals: 0 },
      defaultValue: 10,
    }
  },
  kederSchiene: {
    diameterId: articleFeatureHandlers.diamterId,
    length: {
      mul: true,
      ...articleFeatureHandlers.lengthInM,
      values: { type: "minMax", min: 2, max: 4, step: 1 },
      defaultValue: 2,
    }
  },
  umlaufSeil: {
    colorId: articleFeatureHandlers.colorId,
    length: {
      ...articleFeatureHandlers.lengthInM,
      formatValue: (val) => `${val as number}${Measure.m}=${(val as number) / 2}${Measure.m}`,
    }
  },
  protectiveCoverStandard: {
    sst: articleFeatureHandlers.sst,
    axleLength: articleFeatureHandlers.axleLength,
    quality: articleFeatureHandlers.quality,
  },
  protectiveCoverSpecial: {
    sst: articleFeatureHandlers.sst,
    axleLength: articleFeatureHandlers.axleLength,
    colorId: {
      values: (context) => ClothDataLogic.getProtectionColors(context.articleMap, "user", false),
      additionalArticleInfo: (val, context) => AdditionalArticleInfo.colorId(context, val?.toString() || ""),
      formatValue: (val, context) => FeatureFormatters.renderColorComboItem("" + val, context),
      beosVariants: (val) => (val ? { "41": "" + val } : undefined)
    },
    quality: articleFeatureHandlers.quality,
  },
  cornerProtection: {
    colorId: {
      values: (context) => ClothDataLogic.getProtectionColors(context.articleMap, "user", true),
      additionalArticleInfo: (val, context) => AdditionalArticleInfo.colorId(context, val?.toString() || ""),
      formatValue: (val, context) => FeatureFormatters.renderColorComboItem("" + val, context),
      beosVariants: (val) => (val ? { "41": "" + val } : undefined)
    },
  },
  algaeRemover: {
    volume: articleFeatureHandlers.volumeInML
  },
  rubberRope: {
    diameter: articleFeatureHandlers.diameterInMM,
    colorId: articleFeatureHandlers.colorId,
    length: {
      mul: true,
      ...articleFeatureHandlers.lengthInM,
      values: { type: "slider", min: 3, max: 50, step: 1, decimals: 0 },
      defaultValue: 10,
    }
  },
  spannFlex: {
    colorId: articleFeatureHandlers.colorId,
  },
  spannQuick: {
    colorId: articleFeatureHandlers.colorId,
  },
  clipOnMini: {
    colorId: articleFeatureHandlers.colorId,
  },
  clipOnMidi: {
    colorId: articleFeatureHandlers.colorId,
  },
  motorCable: {
    length: articleFeatureHandlers.lengthInM,
    colorId: articleFeatureHandlers.colorId
  },
  ropeConserver: {
    volume: articleFeatureHandlers.volumeInML
  },
} satisfies Record<string, IArticleFeatureTemplate>

export type IShopFeatureTemplateId = keyof typeof shopTemplates;

export const appTemplates = {
  protectiveCover: {
    sst: { ...articleFeatureHandlers.sst, readOnly: true },
    axleLength: { ...articleFeatureHandlers.axleLength, readOnly: true },
    colorId: {
      save: true,
      values: (context) => ClothDataLogic.getProtectionColors(context.articleMap, "user", true),
      additionalArticleInfo: (val, context) => AdditionalArticleInfo.colorId(context, val?.toString() || ""),
      formatValue: (val, context) => FeatureFormatters.renderColorComboItem("" + val, context),
    },
    quality: articleFeatureHandlers.quality,
  },
  loungeWithLogo: {
    colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) },
    logo: articleFeatureHandlers.logo
  },
  lounge: {
    colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) }
  },
  sojoHW: {
    colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) },
    sojoWoodType: { ...articleFeatureHandlers.sojoWoodType, beosVariants: (val) => (val ? { "42": (val as SojoWoodType) } : undefined) }
  },
  sojoCloth: {
    colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) },
  },
  raffCloth: {
    colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) },
    rainCut: { ...articleFeatureHandlers.rainCut, },
    area: { ...articleFeatureHandlers.areaInM2, mul: true },
  },
  netCloth: {
    netShapeType: articleFeatureHandlers.netShapeType,
    colorId: { ...articleFeatureHandlers.colorId, beosVariants: (val) => (val ? { "41": "" + val } : undefined) },
    area: { ...articleFeatureHandlers.areaInM2, mul: true },
  },
  drahtSeil: {
    length: { mul: true, ...articleFeatureHandlers.lengthInM, },
    startTerminal: articleFeatureHandlers.startTerminal,
    endTerminal: articleFeatureHandlers.endTerminal,
  },
  curtainGuide: {
    length: { mul: true, ...articleFeatureHandlers.lengthToMM, },
    notchSide: articleFeatureHandlers.notchSide,
    // angleLeft: articleFeatureHandlers.angleLeft,
    // angleRight: articleFeatureHandlers.angleRight,
    angleLeft: {
      additionalArticleInfo: (_val, context, store) => {
        const angleLeft = store.angleLeft;
        const angleRight = store.angleRight
        const angles = (angleLeft || angleRight) ? MathUtil.round(MathUtil.rad2deg(angleLeft || 0), 1) + "°/" + MathUtil.round(MathUtil.rad2deg(angleRight || 0), 1) + "°" : ""
        if (!angles)
          return undefined;
        return { header: context.lc.t("ArticleInfo_Angle"), text: angles } as const
      }
    },
  }
} satisfies Record<string, IArticleFeatureTemplate>